import {
  BACKEND_ERROR,
  useAcceptWorkMutation,
  useConfirmPayment,
  useGetReservationByIdQuery,
} from "@stepapp/core";
import { ReservationStatus } from "@stepapp/core/apiTypes";
import { captureSentryException } from "@stepapp/sentry";
import { Block, Button, Image, spacing, Touchable, Typography } from "@stepapp/ui";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import { AppStorage } from "../../../../src/services/AppStorage";
import { useDisplayIntercom } from "../../../../src/services/Intercom";
import { footerStyle } from "../../../../src/styles/themes";

const ServiceConfirmation = () => {
  const { t } = useTranslation();
  const { navigate, back, replace } = useRouter();
  const countryCode = AppStorage().get("countryCode");
  const displayIntercom = useDisplayIntercom(countryCode);
  const stripeConfirmPayment = useConfirmPayment();
  const { reservationId = "" } = useLocalSearchParams<{ reservationId: string }>();
  const [acceptWork] = useAcceptWorkMutation();
  const [loading, setLoading] = useState(false);
  const { data } = useGetReservationByIdQuery({
    fetchPolicy: "network-only",
    variables: { orderId: reservationId, isCustomerApp: true },
  });

  const completedWorkPhotos = data?.getReservationById?.completionPhotos;
  const image = completedWorkPhotos?.[completedWorkPhotos.length - 1]?.file?.url || "";

  const navigateToConfirmation = useCallback(async () => {
    if (data?.getReservationById?.status !== ReservationStatus.CustomerReview) {
      return navigate(`/calendar/${reservationId}/rating`);
    }

    setLoading(true);

    const { errors } = await acceptWork({ variables: { orderId: reservationId } });

    if (errors && errors[0]?.extensions?.type === BACKEND_ERROR.PaymentRequiresAction) {
      const isConfirmed = await stripeConfirmPayment(
        // @ts-expect-error
        errors[0]?.extensions?.paymentNextAction?.clientSecret,
        data?.getReservationById?.payment?.id || "",
        captureSentryException,
      );

      if (!isConfirmed) {
        setLoading(false);
        alert(t("alerts.paymentFailed"));
        return replace("/calendar");
      }

      const { data: acceptWorkData } = await acceptWork({
        variables: { orderId: reservationId },
      });

      setLoading(false);
      if (acceptWorkData) {
        return navigate(`/calendar/${reservationId}/rating`);
      }
      alert(t("alerts.paymentFailed"));
      return replace("/calendar");
    }

    setLoading(false);
    navigate(`/calendar/${reservationId}/rating`);
  }, [
    t,
    navigate,
    replace,
    reservationId,
    acceptWork,
    stripeConfirmPayment,
    data?.getReservationById?.status,
    data?.getReservationById?.payment?.id,
  ]);

  const handleDisplayIntercom = useCallback(() => {
    back();
    setTimeout(() => {
      displayIntercom();
    }, 1000);
  }, [back, displayIntercom]);

  const navigateToImagePreview = useCallback(() => {
    image &&
      navigate({
        pathname: "/image-preview",
        params: { uri: image },
      });
  }, [image, navigate]);

  return (
    <>
      <ScrollView contentContainerStyle={styles.container}>
        <Typography type="primary" variant="largeTitle">
          {t("screens.serviceConfirmation.doYouAcceptService")}
        </Typography>
        <Block marginBottom={2} marginTop={1}>
          <Typography type="secondary" variant="lightLabel">
            {t("screens.serviceConfirmation.completedServicePhoto")}
          </Typography>
        </Block>

        <Touchable onPress={navigateToImagePreview} style={styles.imageContainer}>
          <Image
            contentFit="cover"
            height={spacing.deviceHeight * 0.5}
            source={image}
            style={styles.image}
          />
        </Touchable>
      </ScrollView>

      <Block row style={footerStyle}>
        <Button
          disabled={loading}
          style={styles.footerButton}
          label={t("screens.serviceConfirmation.report")}
          onPress={handleDisplayIntercom}
          type="transparent"
        />
        <Button
          disabled={loading}
          style={styles.footerButton}
          label={t("common.accept")}
          loading={loading}
          onPress={navigateToConfirmation}
          type="primary"
        />
      </Block>
    </>
  );
};

export default ServiceConfirmation;

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.unit * 2,
    paddingBottom: spacing.unit * 15,
  },
  footerButton: {
    flex: 1,
  },
  image: {
    borderRadius: spacing.radius * 2,
    width: "100%",
  },
  imageContainer: {
    borderRadius: spacing.radius * 2,
    display: "flex",
    marginHorizontal: "auto",
    marginVertical: spacing.unit * 2,
    overflow: "hidden",
    position: "relative",
    width: "100%",
  },
});
